import type { NotificationSignalHeaderLabelItem } from "./NotificationSignalHeaderLabel";
import { formatNotificationSignalHeaderEventTypeLabel } from "./notificationSignalHeaderEventType";
import type { HoldingsSignalItem } from "@/src/features/api/NotifyApi";

type LabelGroup = Pick<NotificationSignalHeaderLabelItem, "label" | "signals">;

export type TranslateSignalHeaderEventTypeLabel = (i18nKey: string) => string;

/**
 * Known API `event_type` → i18n key (English default label; must match keys in
 * `@translate/components/Notification/NotificationSignalHeaderEventType`).
 * Resolve at runtime with {@link TranslateSignalHeaderEventTypeLabel} (from `useParams` locale).
 */
const NOTIFICATION_SIGNAL_HEADER_EVENT_TYPE_TAXONOMY = {
  rating_pillar_rank_changed: "Rating Pillar Rank Changed",
  earnings_flash_ready: "Earnings Flash Ready",
  earnings_transcript_ready: "Earnings Transcript Ready",
  alpha_pick_published: "Alpha Pick Published",
  ta_signal_changed: "Ta Signal Changed",
} as const;

type TaxonomyKey = keyof typeof NOTIFICATION_SIGNAL_HEADER_EVENT_TYPE_TAXONOMY;

function labelForEventType(
  eventTypeRaw: string,
  translate?: TranslateSignalHeaderEventTypeLabel,
): string {
  if (
    Object.prototype.hasOwnProperty.call(
      NOTIFICATION_SIGNAL_HEADER_EVENT_TYPE_TAXONOMY,
      eventTypeRaw,
    )
  ) {
    const messageKey =
      NOTIFICATION_SIGNAL_HEADER_EVENT_TYPE_TAXONOMY[
        eventTypeRaw as TaxonomyKey
      ];
    if (translate) {
      return translate(messageKey);
    }
    return formatNotificationSignalHeaderEventTypeLabel(eventTypeRaw);
  }
  return formatNotificationSignalHeaderEventTypeLabel(eventTypeRaw);
}

/**
 * Merge rows that share the same `event_type` into one label chip (same title).
 * Rows without `event_type` stay one chip each (keyed by row index + event_id).
 *
 * @param translate Pass `scoped.t` from `I18nUtil.createScoped(NotificationSignalHeaderEventType, locale)`
 *        so labels follow the active locale; omit in tests for formatter fallback on known types.
 */
export function buildNotificationSignalHeaderLabelItems(
  rows: HoldingsSignalItem[],
  translate?: TranslateSignalHeaderEventTypeLabel,
): NotificationSignalHeaderLabelItem[] {
  const orderedKeys: string[] = [];
  const groups = new Map<string, LabelGroup>();

  rows.forEach((row, index) => {
    const eventTypeRaw = row.event_type?.trim() ?? "";
    const hasEventType = eventTypeRaw.length > 0;
    const groupKey = hasEventType
      ? eventTypeRaw
      : `__no_event_type__:${index}:${row.event_id}`;

    const label = hasEventType
      ? labelForEventType(eventTypeRaw, translate)
      : row.asset_symbol?.trim() ||
        row.category?.trim() ||
        `Signal ${index + 1}`;

    const imageUrl = row.logo?.trim() || undefined;
    const signal = { num: 1, imageUrl };

    const existing = groups.get(groupKey);
    if (existing) {
      existing.signals.push(signal);
      return;
    }

    groups.set(groupKey, { label, signals: [signal] });
    orderedKeys.push(groupKey);
  });

  return orderedKeys.map((key) => {
    const entry = groups.get(key)!;
    return { label: entry.label, signals: entry.signals };
  });
}
